/**
 * /sandbox — cómo probar contra el ambiente de habilitación DIAN.
 *
 * No existe un endpoint "sandbox" aparte: el sandbox ES el mismo
 * POST /api/v1/documents del contrato público (taxor/api/openapi.yaml),
 * apuntando al host de habilitación (APP.sandboxApiUrl) con las MISMAS
 * credenciales del partner. Los documentos van a DIAN-habilitación, no
 * a producción.
 */

import { Beaker, Copy } from 'lucide-react'
import { toast } from 'sonner'
import { AppShell } from '@/components/layout/app-shell'
import { ROUTES, APP } from '@/lib/constants'
import { Link } from 'react-router-dom'

const CURL_SAMPLE = `# Sandbox = POST /api/v1/documents contra el host de habilitación.
# Mismas credenciales (client_id + api_key + hmac_secret) del portal.
BASE_URL="${APP.sandboxApiUrl}"
CLIENT_ID="<tu X-Taxor-Client-Id (UUID)>"
API_KEY="<tu X-Taxor-Api-Key (tk_...)>"
HMAC_SECRET="<tu hmac_secret>"

BODY='{"_meta":{"client_doc_ref":"TEST-001"},"Invoice":{"ID":"SETP990000001","IssueDate":"2026-07-02","IssueTime":"10:30:00-05:00","InvoiceTypeCode":"01","DocumentCurrencyCode":"COP","LineCountNumeric":1,"AccountingSupplierParty":{},"AccountingCustomerParty":{},"LegalMonetaryTotal":{},"InvoiceLine":[]}}'

# Timestamp RFC3339 (tolerancia servidor ±5 min) — NO epoch.
TIMESTAMP=$(date -u +%Y-%m-%dT%H:%M:%SZ)
NONCE=$(uuidgen)
BODY_HASH=$(printf '%s' "$BODY" | shasum -a 256 | cut -d' ' -f1)
PAYLOAD="$TIMESTAMP.POST./api/v1/documents.$BODY_HASH"
SIGNATURE=$(printf '%s' "$PAYLOAD" | openssl dgst -sha256 -hmac "$HMAC_SECRET" | cut -d' ' -f2)

curl -X POST "$BASE_URL/api/v1/documents" \\
  -H "X-Taxor-Client-Id: $CLIENT_ID" \\
  -H "X-Taxor-Api-Key: $API_KEY" \\
  -H "X-Taxor-Timestamp: $TIMESTAMP" \\
  -H "X-Taxor-Nonce: $NONCE" \\
  -H "X-Taxor-Signature: $SIGNATURE" \\
  -H "Idempotency-Key: $(uuidgen)" \\
  -H "Content-Type: application/json" \\
  -d "$BODY"`

export function SandboxPage() {
  return (
    <AppShell>
      <div className="mx-auto max-w-3xl">
        <h1 className="text-3xl font-bold">Sandbox</h1>
        <p className="mt-2 text-sm text-fg-secondary">
          Emite documentos contra DIAN-habilitación sin afectar producción.
        </p>

        <div className="mt-6 rounded-xl border border-border bg-bg-surface p-6">
          <div className="flex items-start gap-3">
            <Beaker className="h-6 w-6 flex-none text-brand-600" />
            <div>
              <p className="text-sm font-semibold">No hay un endpoint "sandbox" separado</p>
              <p className="mt-2 text-sm text-fg-secondary">
                El sandbox es el mismo <code className="font-mono text-xs">POST /api/v1/documents</code>{' '}
                del contrato público, apuntando al host de habilitación{' '}
                <code className="font-mono text-xs">{APP.sandboxApiUrl}</code> con tus mismas
                credenciales (client_id + API key + HMAC secret). Los documentos se
                transmiten al ambiente de habilitación de la DIAN — no tienen validez fiscal.
              </p>
            </div>
          </div>
        </div>

        <div className="mt-6 rounded-xl border border-border bg-bg-surface p-6">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold">curl de ejemplo</h2>
            <button
              type="button"
              onClick={() => { navigator.clipboard.writeText(CURL_SAMPLE); toast.success('Copiado') }}
              className="inline-flex items-center gap-1.5 rounded-md border border-border px-3 py-1.5 text-xs font-medium text-fg-secondary hover:bg-bg-elevated"
            >
              <Copy className="h-3.5 w-3.5" /> Copiar
            </button>
          </div>
          <pre className="mt-3 overflow-auto rounded-md bg-bg-elevated p-4 text-xs font-mono">{CURL_SAMPLE}</pre>
          <p className="mt-3 text-xs text-fg-muted">
            El body del ejemplo está incompleto a propósito — completa emisor, adquiriente,
            totales y líneas según el contrato. Detalle de firma HMAC, DTO completo y SDKs en la{' '}
            <Link to={ROUTES.INTEGRATION_GUIDE} className="text-brand-600 underline">
              guía de integración
            </Link>
            , y el contrato OpenAPI en{' '}
            <a href={`${APP.apiUrl}/openapi.yaml`} target="_blank" rel="noopener" className="text-brand-600 underline">
              openapi.yaml
            </a>
            .
          </p>
        </div>

        <div className="mt-6 rounded-md border border-yellow-300 bg-yellow-50 p-4 text-xs text-yellow-900 dark:border-yellow-800 dark:bg-yellow-950 dark:text-yellow-100">
          <strong>Recordatorio:</strong> el <code className="font-mono">X-Taxor-Timestamp</code> va en
          formato RFC3339 UTC (tolerancia ±5 min) y el <code className="font-mono">X-Taxor-Nonce</code> es
          de un solo uso (anti-replay, TTL 15 min). Un 401 <code className="font-mono">nonce_already_used</code>{' '}
          significa que reusaste el nonce.
        </div>
      </div>
    </AppShell>
  )
}
